import type { CanvasSurfaceLike, RecordAny } from './types';

/** 与 A2UIMessageRenderer 中过滤逻辑一致：A2UI v0.8 服务端信封 */
export function isA2UIEnvelopeMessage(item: unknown): item is RecordAny {
  if (!item || typeof item !== 'object') {
    return false;
  }
  const r = item as Record<string, unknown>;
  return Boolean(r.surfaceUpdate || r.dataModelUpdate || r.beginRendering || r.deleteSurface);
}

export function normalizeCanvasPayload(payload: unknown): Record<string, unknown> | undefined {
  if (!payload) return undefined;
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : undefined;
    } catch {
      return undefined;
    }
  }
  return typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : undefined;
}

export function normalizeCanvasSurface<TSurface extends CanvasSurfaceLike>(surface: TSurface | null | undefined): TSurface | null {
  if (!surface) return null;
  const normalizedPayload = normalizeCanvasPayload(surface.payload);
  if (normalizedPayload === surface.payload) return surface;
  return { ...surface, payload: normalizedPayload };
}

export function deriveA2UIMessagesFromPayload(payloadValue: unknown): Record<string, unknown>[] {
  const payload = normalizeCanvasPayload(payloadValue);
  if (!payload) return [];
  const messageList = payload.messages;
  if (Array.isArray(messageList) && messageList.length > 0) return (messageList as Record<string, unknown>[]).filter(isA2UIEnvelopeMessage);
  const objects = payload.objects;
  if (Array.isArray(objects) && objects.length > 0 && objects.every(isA2UIEnvelopeMessage)) return objects as Record<string, unknown>[];
  return [];
}

/**
 * 从 `canvas_surface.payload` 推导可交给 @a2ui/react `processMessages` 的列表。
 * 支持 `payload.messages`，或与后端一致的 `payload.objects`（信封数组）。
 */
export function deriveA2UIMessagesFromCanvasSurface(surface: CanvasSurfaceLike | undefined): Record<string, unknown>[] {
  return deriveA2UIMessagesFromPayload(surface?.payload);
}
